// Data service ready for Supabase integration
// Currently uses local JSON data, but structured to easily swap with Supabase

import schoolsData from '../data/schools.json';
import eventsData from '../data/events.json';
import ageGroupsData from '../data/ageGroups.json';
import registrationsData from '../data/registrations.json';
import { supabase } from '../lib/supabase';
import bcrypt from 'bcryptjs';

export interface School {
  id: string;
  name: string;
  address: string;
  principal: string;
  contactEmail: string;
  phoneNumber: string;
  region_id?: string | null;
  school_level_id?: string | null;
  school_type?: string | null;
  grade?: string | null;
  region_name?: string | null;
  school_level_name?: string | null;
}

// New sports_registration_app_schools table interface
export interface AppSchool {
  id: string;
  name: string;
  school_level_id: string | null;
  region_id: string | null;
  created_at: string;
  created_by: string;
  region_name?: string | null;
  school_level_name?: string | null;
}

// Region interface for sms_regions table
export interface Region {
  id: string;
  name: string;
}

// School Level interface for sms_school_levels table
export interface SchoolLevel {
  id: string;
  name: string;
}

export interface Event {
  id: string;
  name: string;
  category: string;
  description: string;
}

export interface AgeGroup {
  id: string;
  name: string;
  minAge: number;
  maxAge: number;
}

export interface Registration {
  id: string;
  school_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: 'male' | 'female';
  sport: string; // event id
  age_group: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  created_by: string; // coach id
  status_date?: string; // when status was changed (approved/rejected)
  deleted_at?: string; // when the registration was soft deleted
  // Legacy fields for backward compatibility
  studentId?: string;
  schoolId?: string;
  coachId?: string;
  eventId?: string;
  ageGroupId?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  registrationDate?: string;
  approvedBy?: string | null;
  approvalDate?: string | null;
}

export interface RegistrationFormData {
  schoolId: string;
  eventId: string;
  ageGroupId: string;
  gender: 'male' | 'female';
  firstName: string;
  lastName: string;
  dateOfBirth: string;
}

class DataService {
  private registrations: Registration[] = [...(registrationsData as Registration[])];

  // Schools
  async getSchools(): Promise<School[]> {
    try {
      // Fetch schools, regions, and school levels in parallel
      const [schoolsResult, regionsResult, levelsResult] = await Promise.all([
        supabase
          .from('sms_schools')
          .select('id, name, region_id, school_level_id, school_type, grade')
          .order('name')
          .limit(5000),
        supabase
          .from('sms_regions')
          .select('id, name'),
        supabase
          .from('sms_school_levels')
          .select('id, name')
      ]);

      if (schoolsResult.error) {
        console.error('Supabase error loading schools:', schoolsResult.error);
        throw schoolsResult.error;
      }

      // Create lookup maps for regions and school levels
      const regionsMap = new Map<string, string>();
      if (regionsResult.data) {
        regionsResult.data.forEach(region => {
          regionsMap.set(region.id, region.name);
        });
      }

      const levelsMap = new Map<string, string>();
      if (levelsResult.data) {
        levelsResult.data.forEach(level => {
          levelsMap.set(level.id, level.name);
        });
      }

      if (schoolsResult.data) {
        // Convert Supabase data to our School interface with mapped names
        return schoolsResult.data.map(school => ({
          id: school.id,
          name: school.name || 'Unknown School',
          address: '',
          principal: '',
          contactEmail: '',
          phoneNumber: '',
          region_id: school.region_id,
          school_level_id: school.school_level_id,
          school_type: school.school_type,
          grade: school.grade,
          region_name: school.region_id ? regionsMap.get(school.region_id) || null : null,
          school_level_name: school.school_level_id ? levelsMap.get(school.school_level_id) || null : null
        }));
      }

      // If we get here, there was no error but also no data
      return [];
      
    } catch (error) {
      console.warn('Failed to load schools from Supabase, falling back to local data:', error);
      
      // Fallback to local JSON data if Supabase fails
      return new Promise(resolve => {
        setTimeout(() => resolve([...schoolsData]), 300);
      });
    }
  }

  // Search schools from both Supabase sms_schools and sports_registration_app_schools tables
  async searchSchools(query: string): Promise<{ id: string; name: string; }[]> {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      // Search both tables in parallel
      const [smsResult, appResult] = await Promise.all([
        supabase
          .from('sms_schools')
          .select('id, name')
          .ilike('name', `%${query}%`)
          .limit(10),
        supabase
          .from('sports_registration_app_schools')
          .select('id, name')
          .is('deleted_at', null)
          .ilike('name', `%${query}%`)
          .limit(10)
      ]);

      if (smsResult.error) {
        console.error('Error searching SMS schools:', smsResult.error);
      }
      if (appResult.error) {
        console.error('Error searching App schools:', appResult.error);
      }

      const smsSchools = smsResult.data || [];
      const appSchools = appResult.data || [];
      
      // Combine and sort alphabetically, then limit to 10
      const combined = [...smsSchools, ...appSchools]
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 10);

      return combined;
    } catch (error) {
      console.error('Error searching schools:', error);
      return [];
    }
  }

  // Get school information for a user by their school_id
  async getUserSchool(userId: string): Promise<{ id: string; name: string; } | null> {
    try {
      // First get the user's school_id
      const { data: userData, error: userError } = await supabase
        .from('sports_registration_app_users')
        .select('school_id')
        .eq('id', userId)
        .single();

      if (userError || !userData || !userData.school_id) {
        return null;
      }

      // Then get the school details
      const { data: schoolData, error: schoolError } = await supabase
        .from('sms_schools')
        .select('id, name')
        .eq('id', userData.school_id)
        .single();

      if (schoolError || !schoolData) {
        return null;
      }
      return {
        id: schoolData.id,
        name: schoolData.name
      };
    } catch (error) {
      console.error('Error fetching user school:', error);
      return null;
    }
  }

  async getSchoolById(id: string): Promise<School | null> {
    try {
      const { data, error } = await supabase
        .from('sms_schools')
        .select('id, name')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching school by ID:', error);
        return null;
      }

      // Map database fields to School interface (with defaults for missing fields)
      return {
        id: data.id,
        name: data.name,
        address: '',
        principal: '',
        contactEmail: '',
        phoneNumber: ''
      };
    } catch (error) {
      console.error('Error fetching school by ID:', error);
      return null;
    }
  }

  // Get schools with region information for reports filtering
  async getSchoolsWithRegions(): Promise<Array<{id: string, name: string, region?: string}>> {
    try {
      const { data, error } = await supabase
        .from('sms_schools')
        .select(`
          id, 
          name,
          region_id,
          sms_regions (
            name
          )
        `)
        .order('name')
        .limit(5000);

      if (error) {
        console.error('Supabase error loading schools with regions:', error);
        throw error;
      }

      if (data) {
        return data.map(school => ({
          id: school.id,
          name: school.name || 'Unknown School',
          region: (school.sms_regions as any)?.name || undefined
        }));
      }

      return [];
    } catch (error) {
      console.warn('Failed to load schools with regions from Supabase:', error);
      // Fallback to local data without regions
      return schoolsData.map(school => ({
        id: school.id,
        name: school.name,
        region: undefined
      }));
    }
  }

  // Events
  async getEvents(): Promise<Event[]> {
    return new Promise(resolve => {
      setTimeout(() => resolve([...eventsData]), 300);
    });
  }

  // Age Groups
  async getAgeGroups(): Promise<AgeGroup[]> {
    return new Promise(resolve => {
      setTimeout(() => resolve([...ageGroupsData]), 300);
    });
  }

  // Registrations
  async getRegistrations(filters?: {
    schoolId?: string;
    coachId?: string;
    status?: string;
    eventId?: string;
    ageGroupId?: string;
    gender?: string;
  }): Promise<Registration[]> {
    try {
      // Build Supabase query - exclude soft deleted records
      let query = supabase
        .from('sports_registration_app_registration')
        .select('*')
        .is('deleted_at', null); // Only get records that haven't been soft deleted

      // Apply filters
      if (filters) {
        if (filters.schoolId) {
          query = query.eq('school_id', filters.schoolId);
        }
        if (filters.coachId) {
          query = query.eq('created_by', filters.coachId);
        }
        if (filters.status) {
          query = query.eq('status', filters.status);
        }
        if (filters.eventId) {
          query = query.eq('sport', filters.eventId);
        }
        if (filters.ageGroupId) {
          query = query.eq('age_group', filters.ageGroupId);
        }
        if (filters.gender) {
          query = query.eq('gender', filters.gender);
        }
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching registrations:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      // Convert database records to Registration interface
      const registrations: Registration[] = (data || []).map(record => ({
        id: record.id,
        school_id: record.school_id,
        first_name: record.first_name,
        last_name: record.last_name,
        date_of_birth: record.date_of_birth,
        gender: record.gender,
        sport: record.sport,
        age_group: record.age_group,
        status: record.status,
        created_at: record.created_at,
        created_by: record.created_by,
        status_date: record.status_date,
        deleted_at: record.deleted_at,
        // Legacy fields for backward compatibility
        studentId: `student_${record.id}`,
        schoolId: record.school_id,
        coachId: record.created_by,
        eventId: record.sport,
        ageGroupId: record.age_group,
        firstName: record.first_name,
        lastName: record.last_name,
        dateOfBirth: record.date_of_birth,
        registrationDate: record.created_at?.split('T')[0],
        approvalDate: record.status_date ? record.status_date.split('T')[0] : undefined
      }));

      // Update local cache
      this.registrations = registrations;
      
      return registrations;
    } catch (error) {
      console.error('Error fetching registrations:', error);
      // Fall back to local data if database fails
      return this.registrations;
    }
  }

  async createRegistration(
    registrationData: {
      school_id: string;
      first_name: string;
      last_name: string;
      date_of_birth: string;
      gender: 'male' | 'female';
      sport: string;
      age_group: string;
      status: string;
      created_by: string;
    }
  ): Promise<Registration> {
    try {
      // Insert into Supabase database
      const insertData: any = {
        school_id: registrationData.school_id,
        first_name: registrationData.first_name,
        last_name: registrationData.last_name,
        date_of_birth: registrationData.date_of_birth,
        gender: registrationData.gender,
        sport: registrationData.sport,
        age_group: registrationData.age_group,
        status: registrationData.status,
        created_by: registrationData.created_by
      };

      // If status is approved, set status_date
      if (registrationData.status === 'approved') {
        insertData.status_date = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('sports_registration_app_registration')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (!data) {
        throw new Error('No data returned from database');
      }

      // Convert Supabase response to our Registration interface
      const newRegistration: Registration = {
        id: data.id,
        school_id: data.school_id,
        first_name: data.first_name,
        last_name: data.last_name,
        date_of_birth: data.date_of_birth,
        gender: data.gender,
        sport: data.sport,
        age_group: data.age_group,
        status: data.status,
        created_at: data.created_at,
        created_by: data.created_by,
        status_date: data.status_date,
        // Legacy fields for backward compatibility
        studentId: `student_${data.id}`,
        schoolId: data.school_id,
        coachId: data.created_by,
        eventId: data.sport,
        ageGroupId: data.age_group,
        firstName: data.first_name,
        lastName: data.last_name,
        dateOfBirth: data.date_of_birth,
        registrationDate: data.created_at?.split('T')[0],
        approvalDate: data.status_date ? data.status_date.split('T')[0] : undefined
      };

      // Also add to local array for immediate UI updates
      this.registrations.push(newRegistration);
      
      return newRegistration;
    } catch (error) {
      console.error('Error creating registration:', error);
      throw error;
    }
  }

  async updateRegistrationStatus(
    registrationId: string,
    status: 'approved' | 'rejected' | null,
    approvedBy: string
  ): Promise<Registration> {
    try {
      // Update in Supabase database
      const { data, error } = await supabase
        .from('sports_registration_app_registration')
        .update({ 
          status: status,
          status_date: status ? new Date().toISOString() : null
        })
        .eq('id', registrationId)
        .select()
        .single();

      if (error) {
        console.error('Error updating registration status:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (!data) {
        throw new Error('Registration not found');
      }

      // Convert to Registration interface
      const updatedRegistration: Registration = {
        id: data.id,
        school_id: data.school_id,
        first_name: data.first_name,
        last_name: data.last_name,
        date_of_birth: data.date_of_birth,
        gender: data.gender,
        sport: data.sport,
        age_group: data.age_group,
        status: data.status,
        created_at: data.created_at,
        created_by: data.created_by,
        status_date: data.status_date,
        // Legacy fields for backward compatibility
        studentId: `student_${data.id}`,
        schoolId: data.school_id,
        coachId: data.created_by,
        eventId: data.sport,
        ageGroupId: data.age_group,
        firstName: data.first_name,
        lastName: data.last_name,
        dateOfBirth: data.date_of_birth,
        registrationDate: data.created_at?.split('T')[0],
        approvedBy: approvedBy,
        approvalDate: data.status_date ? data.status_date.split('T')[0] : undefined
      };

      // Update local cache
      const index = this.registrations.findIndex(r => r.id === registrationId);
      if (index !== -1) {
        this.registrations[index] = updatedRegistration;
      }

      return updatedRegistration;
    } catch (error) {
      console.error('Error updating registration status:', error);
      throw error;
    }
  }

  async updateStudentName(
    registrationId: string,
    firstName: string,
    lastName: string
  ): Promise<Registration> {
    try {
      // Update in Supabase database
      const { data, error } = await supabase
        .from('sports_registration_app_registration')
        .update({ 
          first_name: firstName,
          last_name: lastName
        })
        .eq('id', registrationId)
        .select()
        .single();

      if (error) {
        console.error('Error updating student name:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (!data) {
        throw new Error('Registration not found');
      }

      // Convert to Registration interface
      const updatedRegistration: Registration = {
        id: data.id,
        school_id: data.school_id,
        first_name: data.first_name,
        last_name: data.last_name,
        date_of_birth: data.date_of_birth,
        gender: data.gender,
        sport: data.sport,
        age_group: data.age_group,
        status: data.status,
        created_at: data.created_at,
        created_by: data.created_by,
        status_date: data.status_date,
        // Legacy fields for backward compatibility
        studentId: `student_${data.id}`,
        schoolId: data.school_id,
        coachId: data.created_by,
        eventId: data.sport,
        ageGroupId: data.age_group,
        firstName: data.first_name,
        lastName: data.last_name,
        dateOfBirth: data.date_of_birth,
        registrationDate: data.created_at?.split('T')[0],
        approvalDate: data.status_date ? data.status_date.split('T')[0] : undefined
      };

      // Update local cache
      const index = this.registrations.findIndex(r => r.id === registrationId);
      if (index !== -1) {
        this.registrations[index] = updatedRegistration;
      }

      return updatedRegistration;
    } catch (error) {
      console.error('Error updating student name:', error);
      throw error;
    }
  }

  async deleteRegistration(registrationId: string): Promise<void> {
    try {
      // Soft delete in Supabase: Set deleted_at timestamp
      const { error } = await supabase
        .from('sports_registration_app_registration')
        .update({ 
          deleted_at: new Date().toISOString() 
        })
        .eq('id', registrationId);

      if (error) {
        console.error('Error soft deleting registration:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      // Update local cache by removing the deleted registration
      const index = this.registrations.findIndex(r => r.id === registrationId);
      if (index !== -1) {
        this.registrations.splice(index, 1);
      }
    } catch (error) {
      console.error('Failed to delete registration:', error);
      throw error;
    }
  }

  // TODO: Replace with Supabase integration
  // async getSchoolsFromSupabase() {
  //   const { data, error } = await supabase
  //     .from('schools')
  //     .select('*');
  //   
  //   if (error) throw error;
  //   return data;
  // }
  // Admin Methods
  async getAllRegistrations(): Promise<Registration[]> {
    try {
      const { data, error } = await supabase
        .from('sports_registration_app_registration')
        .select('*')
        .is('deleted_at', null) // Only get records that haven't been soft deleted
        .order('created_at', { ascending: false })
        .limit(10000); // Set higher limit to get all registrations

      if (error) {
        console.error('Error fetching all registrations:', error);
        console.error('Error details:', error);
        // Fallback to empty array instead of dummy data
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllRegistrations:', error);
      return [];
    }
  }

  async getAllUsers(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('sports_registration_app_users')
        .select('*')
        .is('deleted_at', null) // Only fetch users that are not soft-deleted
        .order('created_at', { ascending: false })
        .limit(5000); // Set higher limit to get all users

      if (error) {
        console.error('Error fetching all users:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllUsers:', error);
      return [];
    }
  }

  async createUser(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: 'coach' | 'headteacher' | 'admin';
    schoolId: string;
    isActive: boolean;
  }): Promise<any> {
    try {
      // Hash the password before storing in database
      const hashedPassword = await bcrypt.hash(userData.password, 10);
      
      // Map UI role to database role (database only accepts 'coach' or 'headmaster')
      let dbRole: 'coach' | 'headmaster' = userData.role === 'coach' ? 'coach' : 'headmaster';
      if (userData.role === 'headteacher') {
        dbRole = 'headmaster';
      } else if (userData.role === 'admin') {
        dbRole = 'headmaster'; // For now, map admin to headmaster in DB, but we'll track it in the application layer
      }
      
      const { data, error } = await supabase
        .from('sports_registration_app_users')
        .insert([{
          email: userData.email,
          password_hash: hashedPassword,
          first_name: userData.firstName,
          last_name: userData.lastName,
          role: dbRole,
          school_id: userData.schoolId,
          is_active: userData.isActive,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating user:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in createUser:', error);
      throw error;
    }
  }

  async getAllSchools(): Promise<School[]> {
    // This method already exists as getSchools(), just alias it
    return this.getSchools();
  }

  async updateUser(userId: string, userData: {
    email?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    role?: 'coach' | 'headteacher' | 'admin';
    schoolId?: string;
  }): Promise<any> {
    try {
      const updateData: any = {};

      if (userData.email) updateData.email = userData.email;
      if (userData.firstName) updateData.first_name = userData.firstName;
      if (userData.lastName) updateData.last_name = userData.lastName;
      if (userData.role) {
        // Map UI role to database role (database only accepts 'coach' or 'headmaster')
        let dbRole: 'coach' | 'headmaster' = userData.role === 'coach' ? 'coach' : 'headmaster';
        if (userData.role === 'headteacher' || userData.role === 'admin') {
          dbRole = 'headmaster';
        }
        updateData.role = dbRole;
      }
      if (userData.schoolId) updateData.school_id = userData.schoolId;
      if (userData.password) {
        // Hash the password before updating in database
        updateData.password_hash = await bcrypt.hash(userData.password, 10);
      }

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('sports_registration_app_users')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating user:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateUser:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      // Soft delete by setting deleted_at timestamp
      const { error } = await supabase
        .from('sports_registration_app_users')
        .update({ 
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error soft deleting user:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteUser:', error);
      throw error;
    }
  }

  // ========== SPORTS REGISTRATION APP SCHOOLS ==========
  
  // Get all schools from sports_registration_app_schools table
  async getAppSchools(): Promise<AppSchool[]> {
    try {
      const { data, error } = await supabase
        .from('sports_registration_app_schools')
        .select('*')
        .is('deleted_at', null)
        .order('name');

      if (error) {
        console.error('Error fetching app schools:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAppSchools:', error);
      throw error;
    }
  }

  // Add a new school to sports_registration_app_schools table
  async addAppSchool(schoolData: {
    name: string;
    school_level_id?: string | null;
    region_id?: string | null;
    created_by: string;
  }): Promise<AppSchool> {
    try {
      const { data, error } = await supabase
        .from('sports_registration_app_schools')
        .insert([{
          name: schoolData.name,
          school_level_id: schoolData.school_level_id || null,
          region_id: schoolData.region_id || null,
          created_by: schoolData.created_by
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding app school:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in addAppSchool:', error);
      throw error;
    }
  }

  // Soft delete a school from sports_registration_app_schools table
  async deleteAppSchool(schoolId: string, deletedBy: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('sports_registration_app_schools')
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: deletedBy
        })
        .eq('id', schoolId);

      if (error) {
        console.error('Error soft deleting app school:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteAppSchool:', error);
      throw error;
    }
  }

  // Get all regions for dropdown
  async getRegions(): Promise<Region[]> {
    try {
      const { data, error } = await supabase
        .from('sms_regions')
        .select('id, name')
        .order('name');

      if (error) {
        console.error('Error fetching regions:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getRegions:', error);
      throw error;
    }
  }

  // Get all school levels for dropdown
  async getSchoolLevels(): Promise<SchoolLevel[]> {
    try {
      const { data, error } = await supabase
        .from('sms_school_levels')
        .select('id, name')
        .order('name');

      if (error) {
        console.error('Error fetching school levels:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getSchoolLevels:', error);
      throw error;
    }
  }

  //
  // async createRegistrationInSupabase(registration: Registration) {
  //   const { data, error } = await supabase
  //     .from('registrations')
  //     .insert([registration]);
  //   
  //   if (error) throw error;
  //   return data;
  // }
}

export const dataService = new DataService();
